import { zodResolver } from "@hookform/resolvers/zod";
import { ArrowLeft, Check, ChevronRight, Trash2 } from "lucide-react";
import { reaction } from "mobx";
import { observer, useLocalObservable } from "mobx-react-lite";
import { type FC, useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";

import { OptionType, useGetOptionsQuery } from "@/api/admin";
import { AdminSections } from "@/api/admin/teamRoles";
import { CustomButton, GreenSwitch, Loader } from "@/components/atoms";
import { Form, FormControl, FormField, FormItem } from "@/components/ui/form";
import { Sheet, SheetContent, SheetHeader } from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
import { usePermission } from "@/services/casl/usePermission";

import {
  DETAILS_FIELDS_REQUIRED,
  type DishFormType,
  makeDishFormSchema,
  NUTRITION_FIELDS_REQUIRED,
  sortDishImagesByOrder,
} from "../BrandFormMenu/types";
import { MediaStore } from "../MediaUploader";
import { uploadMediaStoreFiles } from "../MediaUploader/uploadMediaStoreFiles";
import ConfirmOverlay from "./ConfirmOverlay";
import type { DishDrawerStore } from "./dishDrawerStore";
import { defaultDishForm, DISH_STEPS, type StepId } from "./dishDrawerStore";
import { DishMediaValidation } from "./steps/dishMediaConfig";
import StepAvailability from "./steps/StepAvailability";
import StepDetails from "./steps/StepDetails";
import StepDietNutrition from "./steps/StepDietNutrition";
import StepMealTaste from "./steps/StepMealTaste";
import StepPhotos from "./steps/StepPhotos";

interface Props {
  store: DishDrawerStore;
  /**
   * Decimals the price field accepts, from the menu's currency. Defaults to 2
   * because most currencies use 2; JOD and KWD use 3.
   */
  priceDecimals?: number;
  /** Symbol of the menu's currency, shown on the price label. */
  currencySymbol?: string;
  onSave: (
    dish: DishFormType,
    branchAvailability: DishDrawerStore["branchAvailability"]
  ) => void | Promise<void>;
  onDelete?: () => void | Promise<void>;
}

const STEP_REQUIRED_FIELDS: Record<StepId, (keyof DishFormType)[]> = {
  1: DETAILS_FIELDS_REQUIRED,
  2: [],
  3: NUTRITION_FIELDS_REQUIRED,
  4: [],
  5: [],
};

const DishDrawer: FC<Props> = observer(
  ({ store, priceDecimals = 2, currencySymbol, onSave, onDelete }) => {
    const {
      isOpen,
      currentStep,
      dish,
      showDiscardConfirm,
      showDeleteConfirm,
      mode,
    } = store;

    // Same currency-driven price rules as the standalone dish form.
    const dishSchema = useMemo(
      () => makeDishFormSchema(priceDecimals),
      [priceDecimals]
    );

    const form = useForm<DishFormType>({
      resolver: zodResolver(dishSchema),
      defaultValues: defaultDishForm,
      mode: "onChange",
      reValidateMode: "onBlur",
    });

    const hasUpdatePermission = usePermission(AdminSections.BRAND, {
      isUpdate: true,
    });

    const { control, getValues, trigger, formState } = form;
    const [isMediaPending, setIsMediaPending] = useState(false);

    const mediaStore = useLocalObservable(
      () => new MediaStore(undefined, DishMediaValidation)
    );

    const { data: optionsData, isPending: isOptionsPending } =
      useGetOptionsQuery([
        OptionType.Diets,
        OptionType.Allergies,
        OptionType.MielTime,
        OptionType.Occasions,
        OptionType.FlagsAndStatuses,
      ]);

    const diets = optionsData?.diets ?? [];
    const allergies = optionsData?.allergies ?? [];
    const mealTimeOptions = optionsData?.mealTimes ?? [];
    const occasionsOptions = optionsData?.occasions ?? [];
    const flagsAndStatuses = optionsData?.flagsAndStatuses ?? [];

    useEffect(() => {
      if (isOpen) {
        form.reset(dish);
      }
    }, [dish, isOpen, form]);

    useEffect(() => {
      if (!isOpen) {
        mediaStore.resetStore();
        return;
      }

      const sorted = sortDishImagesByOrder(dish.images?.media);

      mediaStore.restoreFromPayload(
        sorted.map(media => ({
          id: media.id,
          previewUrl: media.variants[0]?.url ?? "",
          type: media.type,
        }))
      );
    }, [dish.id, isOpen, mediaStore]);

    useEffect(() => {
      const dispose = reaction(
        () => mediaStore.mediaFiles.map(file => file.id),
        () => {
          store.isDirty = true;
        }
      );
      return dispose;
    }, [mediaStore, store]);

    useEffect(() => {
      if (formState.isDirty) {
        store.isDirty = true;
      }
    }, [formState.isDirty, store]);

    const handleOpenChange = useCallback(
      (open: boolean) => {
        if (!open) {
          store.close();
        }
      },
      [store]
    );

    const validateCurrentStep = async (): Promise<boolean> => {
      const fields = STEP_REQUIRED_FIELDS[currentStep];
      if (!fields.length) return true;
      return trigger(fields);
    };

    const handleSave = async () => {
      const validDetails = await trigger(DETAILS_FIELDS_REQUIRED);
      const validNutritients = await trigger(NUTRITION_FIELDS_REQUIRED);

      if (!validDetails) {
        store.goToStep(1);
        return;
      }

      if (!validNutritients) {
        store.goToStep(3);
        return;
      }

      const values = getValues();

      try {
        setIsMediaPending(true);
        const mediaItems = await uploadMediaStoreFiles(mediaStore.mediaFiles);

        const finalDish: DishFormType = {
          ...values,
          mediaItems,
        };

        await onSave(finalDish, store.branchAvailability);
        store.markClean();
        store._forceClose();
      } catch {
        // Error toast is shown by the caller
      } finally {
        setIsMediaPending(false);
      }
    };

    const handleContinue = async () => {
      if (!hasUpdatePermission && currentStep === 5) {
        store.markClean();
        store._forceClose();
        return;
      }

      const valid = await validateCurrentStep();
      if (!valid) return;

      const values = getValues();
      store.setDish(values);

      if (currentStep === 5) {
        await handleSave();
      } else {
        store.nextStep();
      }
    };

    const handleDelete = async () => {
      try {
        await onDelete?.();
        store._forceClose();
      } catch {
        // Error toast is shown by the caller
      }
    };

    const isFinalStep = currentStep === 5;
    const isFirstStep = currentStep === 1;

    const dishTitle =
      dish.dishNameEnglish || (mode === "create" ? "New dish" : "Edit dish");

    const leftButtonProps = isFirstStep
      ? mode === "create"
        ? {
            label: "Cancel",
            onClick: () => store.close(),
            testId: "dish-drawer-cancel",
            className: undefined,
            icon: null,
          }
        : {
            label: "Delete",
            onClick: () => store.requestDelete(),
            testId: "dish-drawer-delete",
            className:
              "text-red-600 bg-red-50 hover:text-red-700 hover:bg-red-200",
            icon: <Trash2 className="w-4 h-4" />,
            disabled: !hasUpdatePermission,
          }
      : {
          label: "Back",
          onClick: () => store.prevStep(),
          testId: "dish-drawer-footer-back",
          className: undefined,
          icon: null,
        };

    const submitButtonContent = isFinalStep ? (
      hasUpdatePermission ? (
        <>
          <Check className="w-4 h-4" />
          {mode === "create" ? "Create dish" : "Save changes"}
        </>
      ) : (
        <>Close</>
      )
    ) : (
      <>
        Continue
        <ChevronRight className="w-4 h-4" />
      </>
    );

    return (
      <>
        <Sheet open={isOpen} onOpenChange={handleOpenChange}>
          <SheetContent
            side="right"
            className="w-full sm:max-w-[1000px] p-0 flex flex-col overflow-hidden"
            onInteractOutside={e => {
              e.preventDefault();
              store.close();
            }}
          >
            <SheetHeader className="px-6 pt-12 pb-0 shrink-0">
              <div className="flex items-start justify-between gap-4">
                <button
                  type="button"
                  onClick={() => store.close()}
                  className="flex items-start gap-3 p-1 rounded-md hover:bg-gray-100 transition-colors shrink-0"
                  data-testid="dish-drawer-close"
                >
                  <ArrowLeft className="w-5 h-5" />
                  <p className="text-lg font-bold leading-snug line-clamp-2">
                    {dishTitle}
                  </p>
                </button>

                <div className="flex items-center gap-3 shrink-0">
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-gray-600">Available</span>
                    <FormField
                      control={control}
                      name="active"
                      render={({ field }) => (
                        <FormItem className="m-0">
                          <FormControl>
                            <GreenSwitch
                              checked={field.value ?? false}
                              onCheckedChange={v => {
                                field.onChange(v);
                                store.toggleMasterAvailability(v);
                              }}
                              disabled={!hasUpdatePermission}
                              data-testid="dish-drawer-master-toggle"
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                  </div>
                </div>
              </div>

              <div className="flex gap-2 mt-4">
                {DISH_STEPS.map(step => {
                  const isCompleted = step.id < currentStep;
                  const isCurrent = step.id === currentStep;
                  return (
                    <div
                      key={step.id}
                      className="flex-1 h-1 bg-gray-100 rounded-full overflow-hidden"
                    >
                      <div
                        className={cn(
                          "h-full rounded-full transition-all duration-500 ease-out",
                          (isCompleted || isCurrent) && "bg-violet-600"
                        )}
                        style={{
                          width: isCompleted
                            ? "100%"
                            : isCurrent
                              ? "100%"
                              : "0%",
                        }}
                      />
                    </div>
                  );
                })}
              </div>

              <div className="flex gap-2 mt-3 overflow-x-auto pb-1 scrollbar-none">
                {DISH_STEPS.map(step => {
                  const isActive = step.id === currentStep;
                  const isDone = step.id < currentStep;
                  return (
                    <button
                      key={step.id}
                      type="button"
                      onClick={() => store.goToStep(step.id)}
                      className={cn(
                        "whitespace-nowrap px-3 py-1 rounded-full text-sm font-medium transition-all cursor-pointer",
                        isActive && "bg-gray-950 text-white",
                        !isActive &&
                          isDone &&
                          "border border-gray-300 text-gray-600 hover:bg-gray-50",
                        !isActive &&
                          !isDone &&
                          "border border-gray-200 text-gray-400 hover:bg-gray-50"
                      )}
                      data-testid={`dish-drawer-step-tab-${step.id}`}
                    >
                      {step.id}. {step.label}
                    </button>
                  );
                })}
              </div>
            </SheetHeader>

            <Form {...form}>
              <form className="flex flex-col flex-1 min-h-0">
                <div className="flex-1 overflow-y-auto px-6 py-6">
                  {currentStep === 1 && (
                    <StepDetails
                      control={control}
                      flagsAndStatuses={flagsAndStatuses}
                      disabled={!hasUpdatePermission}
                      currencySymbol={currencySymbol}
                      priceDecimals={priceDecimals}
                    />
                  )}
                  {currentStep === 2 &&
                    (isOptionsPending ? (
                      <Loader />
                    ) : (
                      <StepMealTaste
                        form={form}
                        mealTimeOptions={mealTimeOptions}
                        occasionsOptions={occasionsOptions}
                        disabled={!hasUpdatePermission}
                      />
                    ))}
                  {currentStep === 3 &&
                    (isOptionsPending ? (
                      <Loader />
                    ) : (
                      <StepDietNutrition
                        control={control}
                        diets={diets}
                        allergies={allergies}
                        disabled={!hasUpdatePermission}
                      />
                    ))}
                  <div className={cn(currentStep !== 4 && "hidden")}>
                    <StepPhotos
                      mediaStore={mediaStore}
                      disabled={!hasUpdatePermission}
                    />
                  </div>
                  {currentStep === 5 && (
                    <StepAvailability
                      store={store}
                      disabled={!hasUpdatePermission}
                    />
                  )}
                </div>

                <div className="shrink-0 border-t px-6 py-4 flex items-center justify-between gap-3 bg-white">
                  <CustomButton
                    variant="light"
                    type="button"
                    onClick={leftButtonProps.onClick}
                    className={leftButtonProps.className}
                    data-testid={leftButtonProps.testId}
                    disabled={leftButtonProps.disabled}
                  >
                    {leftButtonProps.icon}
                    {leftButtonProps.label}
                  </CustomButton>

                  <CustomButton
                    type="button"
                    onClick={handleContinue}
                    data-testid="dish-drawer-continue"
                    disabled={
                      isMediaPending ||
                      (currentStep === 5 &&
                        store.branchAvailability.length === 0)
                    }
                    isPending={isMediaPending}
                  >
                    {submitButtonContent}
                  </CustomButton>
                </div>
              </form>
            </Form>
            {showDiscardConfirm && (
              <ConfirmOverlay
                title="Discard unsaved changes?"
                body="You have unsaved changes. If you close now, they will be lost."
                confirmLabel="Discard changes"
                onCancel={() => store.cancelDiscard()}
                onConfirm={() => store.confirmDiscard()}
              />
            )}

            {showDeleteConfirm && (
              <ConfirmOverlay
                title="Delete dish?"
                body={`This will permanently delete "${dishTitle}". This action cannot be undone.`}
                confirmLabel="Delete dish"
                onCancel={() => store.cancelDelete()}
                onConfirm={handleDelete}
              />
            )}
          </SheetContent>
        </Sheet>
      </>
    );
  }
);

export default DishDrawer;
